import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api, { API } from "@/lib/api";
import { GuestGuideView } from "@/components/GuestGuideView";
import { ArrowLeft, Eye, Pencil, Sparkles, ChevronRight, Loader2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TOUR = [
  { t: "Questa è la tua guida", d: "Tutte le informazioni per i tuoi ospiti, in un'unica pagina elegante." },
  { t: "Modifichi tutto tu", d: "Nella tua struttura potrai personalizzare ogni sezione, testo e foto." },
  { t: "Concierge AI", d: "Un assistente risponde agli ospiti 24/7, usando solo le info della tua guida." },
  { t: "Controlli cosa manca", d: "StayGuide ti mostra cosa aggiungere per una guida perfetta." },
  { t: "Condividi in un tap", d: "Quando sei pronto, condividi con link o QR code." },
];

export default function DemoExplore() {
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [view, setView] = useState("guest");
  const [tour, setTour] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [dup, setDup] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/guide/demo-esempio`).then(({ data }) => setGuide(data.guide)).catch(() => {});
    api.post("/demo/event", { name: "demo_opened" }).catch(() => {});
    try { if (!localStorage.getItem("sg_demo_tour_done")) setShowTour(true); } catch (e) { /* noop */ }
  }, []);

  const logEvent = (name) => { api.post("/demo/event", { name }).catch(() => {}); };
  const switchView = (v) => { setView(v); if (v === "guest") logEvent("demo_guest_previewed"); };

  const endTour = (completed) => {
    setShowTour(false);
    if (completed) logEvent("demo_tour_completed");
    try { localStorage.setItem("sg_demo_tour_done", "1"); } catch (e) { /* noop */ }
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/demo/duplicate", {});
      logEvent("demo_duplicated");
      toast.success("Struttura creata! Completa la tua guida.");
      navigate(`/dashboard/properties/${data.property_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Impossibile creare la struttura"); setBusy(false); }
  };

  if (!guide) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="demo-explore">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100 px-4 py-2.5 flex items-center gap-2">
        <button onClick={() => navigate("/dashboard")} className="text-slate-500" data-testid="demo-back"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span className="text-[10px] font-bold bg-amber-400 text-amber-950 rounded-full px-2 py-0.5">DEMO</span>
          <p className="font-display font-bold text-slate-900 truncate text-sm">Struttura di esempio</p>
        </div>
        <div className="inline-flex bg-slate-100 rounded-full p-0.5">
          <button onClick={() => switchView("host")} data-testid="demo-view-host" className={`rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "host" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}><Pencil className="w-3 h-3" />Host</button>
          <button onClick={() => switchView("guest")} data-testid="demo-view-guest" className={`rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "guest" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}><Eye className="w-3 h-3" />Ospite</button>
        </div>
      </div>

      <div className={`text-center text-xs font-semibold py-1.5 ${view === "guest" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-800"}`}>
        {view === "guest" ? "Vista ospite — così la vedono i tuoi ospiti" : "Vista host — così configuri la tua guida"}
      </div>

      <div className="max-w-md mx-auto bg-white min-h-screen pb-24">
        {view === "guest" ? (
          <GuestGuideView guide={guide} lang="it" bottomNav />
        ) : (
          <div className="p-5 space-y-3" data-testid="demo-host-view">
            <p className="text-sm text-slate-500">Nella tua struttura potrai modificare ogni sezione qui sotto: testi, orari, foto, regole e altro.</p>

            <div className="rounded-2xl border border-slate-100 p-4" data-testid="demo-guide-health">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-800 text-sm">Completezza guida <span className="text-[10px] font-bold bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 ml-1">DEMO</span></p>
                <span className="font-display font-extrabold text-blue-600">92%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: "92%" }} /></div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {["Check-in", "Wi-Fi", "Regole", "Servizi"].map((x) => (
                  <li key={x} className="flex items-center gap-2 text-slate-600"><Check className="w-4 h-4 text-emerald-500" />{x}</li>
                ))}
                <li className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-4 h-4" />Aggiungi informazioni sul parcheggio</li>
              </ul>
              <p className="mt-2 text-[11px] text-slate-400">Dati dimostrativi: nella tua guida vedrai i suggerimenti reali.</p>
            </div>

            {(guide.sections || []).filter((s) => s.is_visible ?? true).map((s) => (
              <div key={s.id} className="rounded-2xl border border-slate-100 p-4 flex items-center justify-between">
                <div><p className="font-semibold text-slate-800 text-sm">{s.title}</p><p className="text-xs text-slate-400">{s.subtitle}</p></div>
                <span className="text-[11px] text-slate-300 font-semibold">Modifica</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-100 p-3">
        <div className="max-w-md mx-auto">
          <button onClick={() => setDup(true)} data-testid="demo-duplicate-cta" className="w-full rounded-full bg-blue-500 text-white font-semibold py-3.5 inline-flex items-center justify-center gap-2"><Sparkles className="w-4 h-4" />Usa questa demo come punto di partenza</button>
        </div>
      </div>

      {showTour && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-end justify-center p-4" data-testid="demo-tour">
          <div className="w-full max-w-md bg-white rounded-3xl p-6">
            <div className="flex items-center justify-between"><span className="text-xs font-semibold text-blue-500">{tour + 1} / {TOUR.length}</span><button onClick={() => endTour(false)} data-testid="demo-tour-skip" className="text-slate-400 text-xs font-semibold">Salta</button></div>
            <h3 className="font-serif-display text-2xl text-slate-900 mt-3">{TOUR[tour].t}</h3>
            <p className="text-slate-500 mt-2">{TOUR[tour].d}</p>
            <button onClick={() => tour < TOUR.length - 1 ? setTour(tour + 1) : endTour(true)} data-testid="demo-tour-next" className="mt-5 w-full rounded-full bg-blue-500 text-white font-semibold py-3 inline-flex items-center justify-center gap-1">{tour < TOUR.length - 1 ? <>Avanti <ChevronRight className="w-4 h-4" /></> : "Crea la mia guida"}</button>
          </div>
        </div>
      )}

      {dup && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setDup(false)} data-testid="demo-dup-modal">
          <div className="w-full max-w-sm bg-white rounded-3xl p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif-display text-xl text-slate-900">Crea la tua guida partendo da questo esempio?</h3>
            <p className="text-sm text-slate-500 mt-2">Creeremo una nuova struttura tua, pronta da personalizzare con i tuoi dati reali.</p>
            <button onClick={duplicate} disabled={busy} data-testid="demo-dup-confirm" className="mt-5 w-full rounded-full bg-blue-500 text-white font-semibold py-3 disabled:opacity-60">{busy ? "Creazione…" : "Crea la mia struttura"}</button>
            <button onClick={() => setDup(false)} className="mt-2 w-full text-sm text-slate-400">Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}
